import {memo, useCallback, useEffect, useMemo, useState} from 'react';
import PageBlock from "../../../components/PageBlock";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import TD from "../../../components/DataTable/TD";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import {useUserContext} from "../../../types/contexts/UserContext";
import {useNavigate} from "react-router-dom";
import {CountriesResponse, Country} from "../../../types/Responses/CountriesResponse";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import CountryAvatar from "../../../components/CountryAvatar";
import Forbidden from "../../../components/Forbidden";
import {useTranslation} from "react-i18next";
import Card from "../../../components/Card";
import {setTitle} from "../../../hooks/setTitle";
import {APILink} from "../../../types/APILink";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import {setSidebarTitle} from "../../../reducers/sidebar";
import {useDispatch} from "react-redux";

const Countries = () => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const user = useUserContext();
    const navigate = useNavigate();
    const toast = useToastContext();
    const handleError = useErrorHandler();
    const [countries, setCountries] = useState<CountriesResponse>();
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const defaultRequest = useMemo<DataTableRequest>(() => ({
        length: 10,
        page: 1,
        sort_by: 'id',
        sort_direction: 0
    }), []);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<DataTableRequest>(defaultRequest);


    const sendRequest = useCallback((request: DataTableRequest) => {
        setIsDataTableLoading(true)
        API.getCountries(request).then((response: AxiosResponse) => {
            setCountries(response.data as CountriesResponse);
            setIsDataTableLoading(false)
        }).catch((e) => ErrorHandler(e, `get${APILink.GET_COUNTRIES}`, toast, handleError, setIsForbidden));
    }, [toast, handleError]);

    const onDataTableChanged = useCallback((request: DataTableRequest) => {
        persistDataTableRequest(request);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...request, search: ''} : request);
        }
    }, [persistDataTableRequest, sendRequest]);

    const deleteCountry = useCallback((country: Country) => {
        if (countries) {
            const filteredArray = {
                ...countries,
                data: countries.data.filter(r => r.id !== country.id),
                count: countries.count - 1
            };
            API.deleteCountry(country.id).then(() => {
                if (country) {
                    setCountries(filteredArray)
                }
            }).catch(e => ErrorHandler(e, `delete${APILink.GET_COUNTRY.replace('$country', country.id.toString())}`, toast, handleError));
        }
    }, [countries, setCountries, toast, handleError]);

    const refresh = useCallback(() => {
        sendRequest({...dataTableRequest});
    }, [dataTableRequest]);

    useEffect(() => {
        setTitle(t('admin.countries.title'));
        dispatch(setSidebarTitle(t('admin.countries.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                const locationSearch = window.location.search.substr(1).split('&');
                if (locationSearch.length > 0) {
                    const newID = locationSearch[0]?.split('=');
                    if (newID?.length) {
                        const request = {...dataTableRequest, search: newID[1]};
                        persistDataTableRequest(request);
                        sendRequest(request);
                    }
                } else {
                    sendRequest(dataTableRequest);
                }
            }).catch();
        }
    }, [t, user, dispatch, setSidebarTitle]);

    const actionsPanel = useMemo(() => {
        return (<div className="flex flex-row gap-2 lg:justify-end">
                <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5"
                        onClick={() => refresh()}>
                    <FontAwesomeIcon
                        icon={solid('refresh')}
                        className="w-12 h-3"/>
                </Button>
                {user?.user.permissions.includes('create country') &&
                    <Button onClick={() => navigate('/app/admin/countries/create')}
                            className="bg-black text-white">{t('admin.countries.new_country.button')}</Button>}
            </div>
        )
    }, [user, refresh]);

    const dataTable = useMemo(() => {
        if (!countries)
            return <></>;

        return <DataTable {...dataTableRequest}
                          total_pages={countries.pages.total}
                          count={countries.count}
                          nodata={countries.data.length === 0}
                          inProgress={isDataTableLoading}
                          rightPanel={actionsPanel}
                          onChange={onDataTableChanged} headers={[
            {name: '#', sortBy: 'id'},
            {name: t('admin.countries.table.name'), sortBy: 'name'},
            {name: t('admin.countries.table.code'), sortBy: 'code'},
            {name: t('admin.countries.table.phone'), sortBy: 'phone'},
            {name: '', className: 'text-right'}
        ]}>
            <tbody>
            {countries.data.map((country, index) => {
                    return <tr key={index}>
                        <TD>{((dataTableRequest.page - 1) * dataTableRequest.length) + index + 1}</TD>
                        <TD><CountryAvatar country={country}/></TD>
                        <TD>{country.code.toUpperCase()}</TD>
                        <TD>{country.phone ? `+${country.phone}` : ''}</TD>
                        <TD className="text-right">
                            <>
                                {user?.user.permissions.includes('update country') &&
                                    <Button onClick={() => navigate(`/app/admin/countries/${country.id}`)}
                                            className="px-5 py-2 text-white bg-black lg:mr-2"><FontAwesomeIcon
                                        icon={solid("pen-to-square")}/></Button>}
                                {user?.user.permissions.includes('delete country') &&
                                    <Button onClick={() => deleteCountry(country)}
                                            className="px-5 py-2 text-red-300 bg-black"><FontAwesomeIcon
                                        icon={solid("trash")}/></Button>}
                            </>
                        </TD>
                    </tr>
                }
            )}
            </tbody>
        </DataTable>
    }, [dataTableRequest, isDataTableLoading, countries, deleteCountry, navigate, onDataTableChanged, user?.user.permissions]);

    return (<PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody className="overflow-auto px-0">
                    {dataTable}
                    <Forbidden isForbidden={isForbidden}/>
                </CardBody>
            </Card>
        </PageBlock>
    );
}
export default memo(Countries);
